import { t } from "@travily/i18n";
import {
  productCreatorStagePath,
  productEditorPath,
  serviceCreatorStagePath,
  serviceEditorPath,
} from "@travily/routes";
import type ListingsCard from "@travily/types/models/ListingsCard";
import type {
  ListingsCardPhotoTrait,
  ListingsCardPricingTrait,
  ListingsCardPublishTrait,
  ListingsCardSetupTrait,
} from "@travily/types/models/ListingsCard";
import { CardTag } from "@ui/components/Card";
import { formatPrice } from "@ui/lib/format-price";
import { ListingCard } from "@/components/ListingCard/ListingCard";

type SellerListing = ListingsCard &
  ListingsCardPhotoTrait &
  ListingsCardPricingTrait &
  ListingsCardSetupTrait &
  ListingsCardPublishTrait;

export type SellerListingCardProps =
  | { type: "product"; listing: SellerListing }
  | { type: "service"; listing: SellerListing };

const STATUS_DOT_COLORS: Record<SellerListing["setup_status"], string> = {
  not_started: "bg-neutral-400",
  in_progress: "bg-orange-500",
  completed: "bg-green-500",
};

// i18n-tasks-use t('components.views.creator.seller_listing_card.status.not_started')
// i18n-tasks-use t('components.views.creator.seller_listing_card.status.in_progress')
// i18n-tasks-use t('components.views.creator.seller_listing_card.status.completed')

function resolveHref(props: SellerListingCardProps): string {
  const { type, listing } = props;

  if (listing.setup_status === "completed") {
    return type === "product" ? productEditorPath(listing.sqid) : serviceEditorPath(listing.sqid);
  }

  const stage = listing.setup_stage ?? "title";
  return type === "product"
    ? productCreatorStagePath(listing.sqid, stage)
    : serviceCreatorStagePath(listing.sqid, stage);
}

function formattedPrice(listing: SellerListing): string | null {
  if (listing.free) return t("components.views.shared.listing_card.free");
  if (listing.price_cents != null && listing.price_currency != null) {
    return formatPrice(listing.price_cents, listing.price_currency);
  }
  return null;
}

function StatusBadge({ status }: { status: SellerListing["setup_status"] }) {
  return (
    <CardTag>
      <span aria-hidden="true" className={`size-2 shrink-0 rounded-full ${STATUS_DOT_COLORS[status]}`} />
      <span>{t(`components.views.creator.seller_listing_card.status.${status}`)}</span>
    </CardTag>
  );
}

function PublishBlockedBadge() {
  return (
    <CardTag>
      <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-amber-500" />
      <span>{t("components.views.creator.seller_listing_card.connect_stripe_to_publish")}</span>
    </CardTag>
  );
}

function isAwaitingStripe(listing: SellerListing): boolean {
  return listing.published_at !== null && !listing.discoverable;
}

export function SellerListingCard(props: SellerListingCardProps) {
  const { listing } = props;
  const href = resolveHref(props);

  return (
    <ListingCard
      href={href}
      image={listing.photo}
      linkLabel={listing.title ?? ""}
      overlayTopLeft={
        isAwaitingStripe(listing) ? <PublishBlockedBadge /> : <StatusBadge status={listing.setup_status} />
      }
      price={formattedPrice(listing)}
      title={listing.title ?? ""}
    />
  );
}
